import type { ReactNode } from 'react';
import { Input, Switch, NumericTextBox } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useFormBuilder } from '../context/FormBuilderContext';

const FORM_TYPES = ['Client', 'Employee'];
const NOTE_SIZE_DEFAULT = 500;

function Label({ children }: { children: ReactNode }) {
  return (
    <label
      style={{
        display: 'block',
        fontSize: 'var(--kendo-font-size-xs)',
        fontWeight: 'var(--kendo-font-weight-bold)',
        color: 'var(--kendo-color-subtle)',
        textTransform: 'uppercase',
        marginBottom: 'var(--kendo-spacing-1)',
      }}
    >
      {children}
    </label>
  );
}

function FlagToggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div style={{ flexShrink: 0 }}>
      <Label>{label}</Label>
      <Switch checked={checked} onChange={(e) => onChange(e.value)} disabled={disabled} />
    </div>
  );
}

/**
 * Renders below the Toolbar when the host supplies `callbacks.onUpdateDefinition`.
 *
 * Row 1 — identity fields: Name, Form Type (locked after creation), Description.
 * Row 2 — feature flags + Note Size: all boolean toggles and the note size input.
 *
 * Form Type is only editable when `isNewForm` is true — changing it after the
 * fact would invalidate version history and any type-specific field logic.
 */
export function FormDetailsBar() {
  const { formDefinition, onUpdateDefinition, readOnly, isNewForm } = useFormBuilder();
  if (!onUpdateDefinition) return null;

  const updateDef = onUpdateDefinition;
  const flags = formDefinition.flags ?? {};

  function patchFlags(patch: Partial<typeof flags>) {
    updateDef({ flags: { ...flags, ...patch } });
  }

  return (
    <div
      style={{
        padding: 'var(--kendo-spacing-2) var(--kendo-spacing-4)',
        background: 'var(--kendo-color-app-surface)',
        color: 'var(--kendo-color-on-app-surface)',
        borderBottom: '1px solid var(--kendo-color-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--kendo-spacing-2)',
        flexShrink: 0,
      }}
    >
      {/* Row 1: identity */}
      <div style={{ display: 'flex', gap: 'var(--kendo-spacing-4)', alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 280px' }}>
          <Label>Name</Label>
          <Input
            value={formDefinition.name}
            onChange={(e) => updateDef({ name: String(e.value ?? '') })}
            disabled={readOnly}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <Label>Form Type *</Label>
          <DropDownList
            data={FORM_TYPES}
            value={formDefinition.formType}
            defaultItem="— Select type —"
            onChange={(e) => {
              if (FORM_TYPES.includes(e.value as string)) {
                updateDef({ formType: e.value as string });
              }
            }}
            disabled={readOnly || !isNewForm}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Label>Description</Label>
          <Input
            value={formDefinition.description ?? ''}
            onChange={(e) => updateDef({ description: String(e.value ?? '') })}
            disabled={readOnly}
            placeholder="What is this form used for?"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {/* Row 2: feature flags + note size */}
      <div style={{ display: 'flex', gap: 'var(--kendo-spacing-6)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <FlagToggle
          label="Show Physicians Orders"
          checked={flags.showOrders ?? false}
          onChange={(v) => patchFlags({ showOrders: v })}
          disabled={readOnly}
        />
        <FlagToggle
          label="Include Medical Reconciliation"
          checked={flags.showMedRec ?? false}
          onChange={(v) => patchFlags({ showMedRec: v })}
          disabled={readOnly}
        />
        <FlagToggle
          label="Show Intake Nav Bar"
          checked={flags.showIntake ?? false}
          onChange={(v) => patchFlags({ showIntake: v })}
          disabled={readOnly}
        />
        <FlagToggle
          label="Join Video Session Nav Bar"
          checked={flags.showVirtualVisitLink ?? false}
          onChange={(v) => patchFlags({ showVirtualVisitLink: v })}
          disabled={readOnly}
        />
        <FlagToggle
          label="Is Incident Form"
          checked={flags.isIncidentForm ?? false}
          onChange={(v) => patchFlags({ isIncidentForm: v })}
          disabled={readOnly}
        />
        <FlagToggle
          label="No Copy"
          checked={flags.noCopy ?? false}
          onChange={(v) => patchFlags({ noCopy: v })}
          disabled={readOnly}
        />
        <div style={{ flex: '0 0 120px' }}>
          <Label>Note Size</Label>
          <NumericTextBox
            value={formDefinition.noteSize ?? NOTE_SIZE_DEFAULT}
            onChange={(e) => updateDef({ noteSize: e.value ?? NOTE_SIZE_DEFAULT })}
            min={0}
            max={32767}
            format="n0"
            disabled={readOnly}
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}
